import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Briefcase,
  Calculator,
  CalendarClock,
  Check,
  CheckCircle2,
  CloudLightning,
  DollarSign,
  Download,
  Gauge,
  Handshake,
  Info,
  KanbanSquare,
  Lightbulb,
  Link2,
  Loader2,
  LogOut,
  Percent,
  Receipt,
  RotateCcw,
  ShieldCheck,
  Timer,
  TrendingUp,
  Wallet,
  Wrench,
} from "lucide-react";

import FinancialModelSaved from "@/components/FinancialModelSaved";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { useOrgDoor } from "@/hooks/useOrgDoor";
import { hasOperatorAccess, isStaffUser } from "@/lib/access";
import { ApiError, api } from "@/lib/api";
import { orgScopedPath } from "@/lib/orgDoor";
import { SBA_MIN_DSCR, type CashFlowBasis } from "@/lib/sbaMath";
import { cn } from "@/lib/utils";
import {
  HOLD_YEARS,
  MAX_DOLLARS,
  MODEL_DEFAULTS,
  SCENARIO_ORDER,
  SCENARIO_SHIFTS,
  SENSITIVITY_GROWTH_STEPS,
  SENSITIVITY_MULTIPLE_STEPS,
  computeModel,
  describeScenario,
  irrLabel,
  modelCsv,
  modelInsights,
  modelSummaryText,
  scenarioInputs,
  sensitivityGrid,
  toModelQuery,
  toSbaQueryFromModel,
  type InsightTone,
  type ModelInputs,
  type ReturnOutcome,
  type ScenarioKey,
} from "@/lib/financialModelMath";

type NumKey = {
  [K in keyof ModelInputs]: ModelInputs[K] extends number ? K : never;
}[keyof ModelInputs];

type Flags = Pick<ModelInputs, "sellerNoteStandby" | "growYear1" | "cashFlowBasis">;

interface FieldDef {
  key: NumKey;
  label: string;
  hint: string;
  icon: typeof DollarSign;
  /** Dollar fields render with thousands separators. */
  money?: boolean;
  suffix?: string;
  step: string;
  min: number;
  max: number;
  integer?: boolean;
}

const dollars = (key: NumKey, label: string, hint: string, icon: typeof DollarSign): FieldDef => ({
  key, label, hint, icon, money: true, min: 0, max: MAX_DOLLARS, step: "1000",
});
const pct = (key: NumKey, label: string, hint: string, min: number, max: number, step = "0.5", icon = Percent): FieldDef => ({
  key, label, hint, icon, suffix: "%", min, max, step,
});

const F: Record<NumKey, FieldDef> = {
  purchasePrice: dollars("purchasePrice", "Purchase Price", "What you pay for the business", DollarSign),
  closingCostsPct: pct("closingCostsPct", "Closing Costs", "% of price, financed into the loan", 0, 20),
  workingCapital: dollars("workingCapital", "Working Capital Reserve", "Financed cash that covers lean years — what's left comes back at exit", Wallet),
  downPaymentPct: pct("downPaymentPct", "Down Payment", "Your cash — % of total project cost", 0, 100),
  interestRate: pct("interestRate", "SBA Rate", "Annual rate — floats with prime", 0, 30, "0.1"),
  loanTermYears: { key: "loanTermYears", label: "SBA Term", hint: "Up to 10 yrs for a business", icon: CalendarClock, suffix: "yrs", min: 1, max: 25, step: "1", integer: true },
  sellerNotePct: pct("sellerNotePct", "Seller Note", "% of price the seller carries", 0, 100, "0.5", Handshake),
  sellerNoteRate: pct("sellerNoteRate", "Note Rate", "Annual rate on the note", 0, 30, "0.1"),
  sellerNoteTermYears: { key: "sellerNoteTermYears", label: "Note Term", hint: "Amortization once payments start", icon: CalendarClock, suffix: "yrs", min: 1, max: 25, step: "1", integer: true },
  revenue: dollars("revenue", "Trailing Revenue", "Last 12 months", TrendingUp),
  revenueGrowthPct: pct("revenueGrowthPct", "Revenue Growth", "Per year, compounding", -50, 100, "0.5", TrendingUp),
  ebitdaMarginPct: pct("ebitdaMarginPct", "EBITDA Margin", "After paying a manager a market salary", -100, 100),
  ownerSalary: dollars("ownerSalary", "Your Salary", "What you'll pay yourself to run it — comes off SDE", Briefcase),
  capexPct: pct("capexPct", "Capex", "Maintenance spend, % of revenue", 0, 50, "0.5", Wrench),
  taxRatePct: pct("taxRatePct", "Tax Rate", "Federal + state on taxable income", 0, 60, "0.5", Receipt),
  equipmentPct: pct("equipmentPct", "Price in Equipment", "Written off over 7 yrs — the rest is goodwill, 15 yrs", 0, 100),
  shockYear: { key: "shockYear", label: "Bad Year", hint: "", icon: CloudLightning, min: 0, max: HOLD_YEARS, step: "1", integer: true },
  shockRevenuePct: pct("shockRevenuePct", "Revenue Drop", "That year, vs. the trend", 0, 90, "1", CloudLightning),
  shockMarginPts: { key: "shockMarginPts", label: "Margin Hit", hint: "Points lost — fixed costs don't shrink", icon: Percent, suffix: "pts", min: 0, max: 100, step: "0.5" },
  exitMultiple: { key: "exitMultiple", label: "Exit Multiple", hint: "× Year 5 EBITDA", icon: LogOut, suffix: "×", min: 0.5, max: 20, step: "0.1" },
  exitCostsPct: pct("exitCostsPct", "Exit Costs", "Broker + legal at sale, % of price", 0, 30),
};

const NUM_KEYS = Object.keys(F) as NumKey[];
const STORAGE_KEY = "financial-model:v2";

const usd = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
/** Table cells: $1.3M / $286K — exact figures live in the CSV export. */
const compact = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: Math.abs(n) < 1e6 ? 0 : 2 }).format(n);
const pctText = (n: number | null) => (n === null ? "n/a" : `${(n * 100).toFixed(1)}%`);
const mult = (n: number | null) => (n === null ? "n/a" : `${n.toFixed(2)}×`);

/** "1100000" → "1,100,000" while keeping a trailing "." or "-" the user is mid-typing. */
function withCommas(raw: string): string {
  const m = /^(-?)(\d*)(\.\d*)?$/.exec(raw);
  if (!m) return raw;
  return m[1] + (m[2] ? Number(m[2]).toLocaleString("en-US") : "") + (m[3] ?? "");
}

type Tone = "excellent" | "good" | "ok" | "risk" | "neutral";

// Translucent status hues so they read on both the light and dark theme.
const toneClasses: Record<Tone, string> = {
  excellent: "border-emerald-500/30 bg-emerald-500/10 text-emerald-500",
  good: "border-sky-500/30 bg-sky-500/10 text-sky-500",
  ok: "border-amber-500/30 bg-amber-500/10 text-amber-500",
  risk: "border-rose-500/30 bg-rose-500/10 text-rose-500",
  neutral: "border-border/60 bg-card/70 text-foreground",
};
const toneText: Record<Tone, string> = {
  excellent: "text-emerald-500",
  good: "text-sky-500",
  ok: "text-amber-500",
  risk: "text-rose-500",
  neutral: "text-foreground",
};

const irrTone = (v: number | null, outcome: ReturnOutcome = "profit"): Tone =>
  outcome !== "profit" ? "risk" : v === null ? "neutral" : v >= 0.25 ? "excellent" : v >= 0.15 ? "good" : v >= 0 ? "ok" : "risk";
const dscrTone = (v: number | null): Tone => (v === null ? "neutral" : v >= 2 ? "excellent" : v >= 1.5 ? "good" : v >= SBA_MIN_DSCR ? "ok" : "risk");

const insightStyle: Record<InsightTone, { icon: typeof Info; cls: string }> = {
  good: { icon: CheckCircle2, cls: "text-emerald-500" },
  info: { icon: Info, cls: "text-primary" },
  warn: { icon: AlertTriangle, cls: "text-amber-500" },
  risk: { icon: AlertTriangle, cls: "text-rose-500" },
};

function readStored(): Partial<ModelInputs> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const toStrings = (i: ModelInputs) => Object.fromEntries(NUM_KEYS.map((k) => [k, String(i[k])])) as Record<NumKey, string>;
const toFlags = (i: ModelInputs): Flags => ({ sellerNoteStandby: i.sellerNoteStandby, growYear1: i.growYear1, cashFlowBasis: i.cashFlowBasis });

interface FinancialModelFormProps {
  /** Pre-fill inputs (shared link or a listing). Omitted fields fall back to defaults. */
  initialValues?: Partial<ModelInputs>;
  /** Remember the last scenario in this browser (the standalone page only). */
  persist?: boolean;
  /** Shown above the inputs, e.g. how a listing's figures were mapped in. */
  note?: ReactNode;
  /** Built from a listing: saves attach to it, and Operator buyers can drop it into their Deal CRM. */
  listing?: { id: string; title: string | null };
}

export default function FinancialModelForm({ initialValues, persist, note, listing }: FinancialModelFormProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { slug } = useOrgDoor();

  // Precedence: explicit initial values (shared link / listing) > this browser's last scenario > defaults.
  const [boot] = useState<ModelInputs>(() => {
    const hasExplicit = !!initialValues && Object.keys(initialValues).length > 0;
    const stored = persist && !hasExplicit ? readStored() : null;
    return { ...MODEL_DEFAULTS, ...(stored ?? {}), ...initialValues };
  });
  const [values, setValues] = useState<Record<NumKey, string>>(() => toStrings(boot));
  const [flags, setFlags] = useState<Flags>(() => toFlags(boot));
  const [scenario, setScenario] = useState<ScenarioKey>("base");
  const [copied, setCopied] = useState(false);
  const [crmBusy, setCrmBusy] = useState(false);
  // A shared link pre-fills the form without replacing this browser's saved
  // scenario — that only happens once the buyer actually changes something.
  const [dirty, setDirty] = useState(false);

  const setField = (key: NumKey, raw: string) => {
    setValues((v) => ({ ...v, [key]: raw }));
    setDirty(true);
  };
  const setFlag = <K extends keyof Flags>(key: K, value: Flags[K]) => {
    setFlags((f) => ({ ...f, [key]: value }));
    setDirty(true);
  };
  const loadInputs = (partial: Partial<ModelInputs>) => {
    const merged = { ...MODEL_DEFAULTS, ...partial };
    setValues(toStrings(merged));
    setFlags(toFlags(merged));
    setScenario("base");
    setDirty(true);
  };

  const errors = useMemo(() => {
    const e: Partial<Record<NumKey, string>> = {};
    for (const f of Object.values(F)) {
      const raw = values[f.key].trim();
      if (raw === "") { e[f.key] = "Required"; continue; }
      const n = Number(raw);
      if (!Number.isFinite(n)) { e[f.key] = "Enter a number"; continue; }
      if (n < f.min) e[f.key] = `Must be ≥ ${f.min.toLocaleString()}`;
      else if (n > f.max) e[f.key] = `Must be ≤ ${f.max.toLocaleString()}`;
      else if (f.integer && !Number.isInteger(n)) e[f.key] = "Whole numbers only";
    }
    if (!e.purchasePrice && Number(values.purchasePrice) <= 0) e.purchasePrice = "Must be greater than 0";
    if (!e.revenue && Number(values.revenue) <= 0) e.revenue = "Must be greater than 0";
    if (!e.downPaymentPct && !e.sellerNotePct && Number(values.downPaymentPct) + Number(values.sellerNotePct) > 100)
      e.sellerNotePct = "Down payment + seller note can't exceed 100%";
    return e;
  }, [values]);

  const baseInputs = useMemo<ModelInputs | null>(() => {
    if (Object.keys(errors).length) return null;
    const nums = Object.fromEntries(NUM_KEYS.map((k) => [k, Number(values[k])])) as Record<NumKey, number>;
    return { ...nums, ...flags };
  }, [errors, values, flags]);

  // The scenario switch re-runs the whole page on shifted assumptions.
  const inputs = useMemo(() => (baseInputs ? scenarioInputs(baseInputs, scenario) : null), [baseInputs, scenario]);
  const result = useMemo(() => (inputs ? computeModel(inputs) : null), [inputs]);
  const insights = useMemo(() => (inputs && result ? modelInsights(inputs, result) : []), [inputs, result]);
  const scenarios = useMemo(
    () => (baseInputs ? SCENARIO_ORDER.map((k) => ({ key: k, r: computeModel(scenarioInputs(baseInputs, k)) })) : []),
    [baseInputs],
  );
  const grid = useMemo(() => (inputs ? sensitivityGrid(inputs) : []), [inputs]);

  useEffect(() => {
    if (!persist || !dirty || !baseInputs) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(baseInputs)); } catch { /* private mode etc. */ }
  }, [persist, dirty, baseInputs]);

  const reset = () => {
    loadInputs({});
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  const shareUrl = (i: ModelInputs) => `${window.location.origin}${orgScopedPath("/buyer/financial-model", slug)}?${toModelQuery(i)}`;

  const copyLink = async () => {
    if (!baseInputs) return;
    const url = shareUrl(baseInputs);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const downloadCsv = () => {
    if (!inputs || !result) return;
    const blob = new Blob([modelCsv(inputs, result, SCENARIO_SHIFTS[scenario].label)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `financial-model-${scenario}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const openSba = () => {
    if (!baseInputs) return;
    navigate(`${orgScopedPath("/buyer/sba-calculator", slug)}?${toSbaQueryFromModel(baseInputs)}`);
  };

  // The CRM is an Operator feature, and a deal there is the buyer's own (buyer, listing) pair.
  const canSendToCrm = !!listing && !!user && (user.roles ?? []).includes("buyer") && !isStaffUser(user) && hasOperatorAccess(user);
  const sendToCrm = async () => {
    if (!listing || !user || !inputs || !result || !baseInputs) return;
    setCrmBusy(true);
    try {
      await api.post("/deal-pipeline/notes", {
        listing_id: listing.id,
        buyer_id: user.id,
        card_id: null,
        body: modelSummaryText(inputs, result, SCENARIO_SHIFTS[scenario].label, shareUrl(baseInputs)),
      });
      toast.success("Model saved to this deal's notes in your Deal CRM");
    } catch (e) {
      toast.error(
        e instanceof ApiError && e.status === 400
          ? "Favorite this listing or express interest first — that's what puts it on your Deal CRM board."
          : "Couldn't save to your Deal CRM",
      );
    } finally {
      setCrmBusy(false);
    }
  };

  const renderField = (f: FieldDef, overrides?: Partial<Pick<FieldDef, "label" | "hint">>) => {
    const Icon = f.icon;
    const err = errors[f.key];
    const id = `fm-${f.key}`;
    return (
      <div key={f.key}>
        <Label htmlFor={id} className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          {overrides?.label ?? f.label}
        </Label>
        <div className="relative">
          {f.money && (
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">$</span>
          )}
          {f.money ? (
            <Input
              id={id}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={withCommas(values[f.key])}
              onChange={(e) => setField(f.key, e.target.value.replace(/[^\d.-]/g, ""))}
              className={cn("pl-8", err && "border-rose-400/70 focus-visible:ring-rose-300/40")}
              aria-invalid={!!err}
            />
          ) : (
            <Input
              id={id}
              type="number"
              inputMode="decimal"
              step={f.step}
              min={f.min}
              max={f.max}
              value={values[f.key]}
              onChange={(e) => setField(f.key, e.target.value)}
              className={cn(f.suffix && "pr-12", err && "border-rose-400/70 focus-visible:ring-rose-300/40")}
              aria-invalid={!!err}
            />
          )}
          {f.suffix && (
            <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">{f.suffix}</span>
          )}
        </div>
        <p className={cn("mt-1 text-[0.8125rem] leading-5", err ? "text-rose-500" : "text-muted-foreground")}>{err ?? overrides?.hint ?? f.hint}</p>
      </div>
    );
  };

  const hasNote = Number(values.sellerNotePct) > 0;
  const isSde = flags.cashFlowBasis === "sde";
  const shockYear = Number(values.shockYear) || 0;
  const years = result?.years ?? [];
  const sba = result?.sba;
  const anyReserveDraw = years.some((y) => y.reserveDraw > 0);
  const anyShortfall = years.some((y) => y.shortfall > 0);

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.5fr)]">
      {/* ── Inputs ─────────────────────────────────────────────── */}
      <div className="space-y-6">
        <Card className="rounded-3xl">
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-lg">Deal inputs</CardTitle>
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1.5 rounded-full border border-border/70 px-3.5 py-1.5 text-sm font-semibold text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </button>
          </CardHeader>
          <CardContent className="space-y-6">
            {note && <div className="rounded-2xl border border-primary/25 bg-primary/10 px-4 py-3 text-sm leading-6 text-muted-foreground">{note}</div>}

            <Section title="Purchase & SBA loan">
              {renderField(F.purchasePrice)}
              <Pair>{renderField(F.downPaymentPct)}{renderField(F.closingCostsPct)}</Pair>
              <Pair>{renderField(F.interestRate)}{renderField(F.loanTermYears)}</Pair>
              {renderField(F.workingCapital)}
            </Section>

            <Section title="Seller financing">
              {renderField(F.sellerNotePct)}
              {hasNote && (
                <>
                  <Pair>{renderField(F.sellerNoteRate)}{renderField(F.sellerNoteTermYears)}</Pair>
                  <Toggle
                    id="fm-standby"
                    checked={flags.sellerNoteStandby}
                    onChange={(v) => setFlag("sellerNoteStandby", v)}
                    icon={Handshake}
                    label="Note on full standby"
                    hint="No payments while the SBA loan is outstanding — interest accrues and the balance is paid from exit proceeds"
                  />
                </>
              )}
            </Section>

            <Section title="Operations">
              {renderField(F.revenue)}
              <Toggle
                id="fm-grow-y1"
                checked={flags.growYear1}
                onChange={(v) => setFlag("growYear1", v)}
                icon={TrendingUp}
                label="Grow Year 1 from the trailing figure"
                hint="Off: Year 1 = the last 12 months. On: Year 1 already includes a year of growth"
              />
              <div>
                <div className="mb-1.5 text-sm font-semibold text-foreground">Earnings basis</div>
                <div className="inline-flex rounded-full border border-border/60 bg-card/70 p-1" role="group" aria-label="Earnings basis">
                  {(["ebitda", "sde"] as CashFlowBasis[]).map((b) => (
                    <button
                      key={b}
                      type="button"
                      aria-pressed={flags.cashFlowBasis === b}
                      onClick={() => setFlag("cashFlowBasis", b)}
                      className={cn(
                        "rounded-full px-4 py-1.5 text-sm font-semibold transition",
                        flags.cashFlowBasis === b ? "bg-gold-gradient text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {b === "sde" ? "SDE (I'll run it)" : "EBITDA (manager runs it)"}
                    </button>
                  ))}
                </div>
              </div>
              <Pair>
                {renderField(F.revenueGrowthPct)}
                {renderField(F.ebitdaMarginPct, isSde ? { label: "SDE Margin", hint: "Before paying yourself" } : undefined)}
              </Pair>
              {isSde && renderField(F.ownerSalary)}
              <Pair>{renderField(F.capexPct)}{renderField(F.taxRatePct)}</Pair>
              {renderField(F.equipmentPct)}
            </Section>

            <Section title="Stress a bad year">
              <div>
                <Label htmlFor="fm-shockYear" className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-foreground">
                  <CloudLightning className="h-3.5 w-3.5 text-muted-foreground" /> Bad year
                </Label>
                <select
                  id="fm-shockYear"
                  value={String(shockYear)}
                  onChange={(e) => setField("shockYear", e.target.value)}
                  className="flex h-11 w-full rounded-2xl border border-border/80 bg-card/85 px-4 text-sm text-foreground transition focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25"
                >
                  <option value="0">None</option>
                  {Array.from({ length: HOLD_YEARS }, (_, k) => (
                    <option key={k + 1} value={k + 1}>Year {k + 1}</option>
                  ))}
                </select>
                <p className="mt-1 text-[0.8125rem] leading-5 text-muted-foreground">A recession or a lost customer — one year off trend, then back on it</p>
              </div>
              {shockYear > 0 && <Pair>{renderField(F.shockRevenuePct)}{renderField(F.shockMarginPts)}</Pair>}
            </Section>

            <Section title="Exit (end of Year 5)">
              <Pair>{renderField(F.exitMultiple)}{renderField(F.exitCostsPct)}</Pair>
            </Section>
          </CardContent>
        </Card>

        {/* Sources & uses — where the money comes from */}
        <Card className="rounded-3xl">
          <CardHeader>
            <CardTitle className="text-lg">Sources & uses</CardTitle>
          </CardHeader>
          <CardContent>
            <Row label="Purchase price" value={sba ? usd(sba.askingPrice) : "—"} />
            <Row label="Closing costs (financed)" value={sba ? usd(sba.closingCosts) : "—"} />
            {sba && sba.workingCapital > 0 && <Row label="Working capital reserve (financed)" value={usd(sba.workingCapital)} />}
            <Row label="Total project cost" value={sba ? usd(sba.totalProjectCost) : "—"} strong />
            <Row label="Your down payment" value={sba ? usd(sba.downPayment) : "—"} />
            <Row label="Seller note" value={sba ? usd(sba.sellerNote) : "—"} />
            <Row label="SBA loan (incl. guaranty fee)" value={sba ? usd(sba.loanAmount) : "—"} />
          </CardContent>
        </Card>

        <FinancialModelSaved
          current={baseInputs}
          onLoad={(partial, name) => {
            loadInputs(partial);
            toast.success(`Loaded "${name}"`);
          }}
          listingId={listing?.id}
          defaultName={listing?.title ?? undefined}
        />
      </div>

      {/* ── Results ────────────────────────────────────────────── */}
      <div className="min-w-0 space-y-6">
        {/* Scenario switch + actions */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex flex-wrap rounded-full border border-border/60 bg-card/70 p-1" role="group" aria-label="Scenario">
            {SCENARIO_ORDER.map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={scenario === k}
                onClick={() => setScenario(k)}
                className={cn(
                  "rounded-full px-4 py-1.5 text-sm font-semibold transition",
                  scenario === k ? "bg-gold-gradient text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {SCENARIO_SHIFTS[k].label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <ActionButton onClick={downloadCsv} disabled={!result} icon={Download}>Export CSV</ActionButton>
            <ActionButton onClick={copyLink} disabled={!baseInputs} icon={copied ? Check : Link2}>{copied ? "Link copied" : "Share"}</ActionButton>
            {canSendToCrm && (
              <ActionButton onClick={sendToCrm} disabled={!result || crmBusy} icon={crmBusy ? Loader2 : KanbanSquare}>Save to Deal CRM</ActionButton>
            )}
            <button
              type="button"
              onClick={openSba}
              disabled={!baseInputs}
              className="inline-flex items-center gap-1.5 rounded-xl bg-gold-gradient px-4 py-2 text-sm font-bold text-primary-foreground shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              <Calculator className="h-3.5 w-3.5" /> Open in SBA Calculator
            </button>
          </div>
        </div>
        {scenario !== "base" && (
          <p className="-mt-3 text-sm leading-6 text-muted-foreground">
            {SCENARIO_SHIFTS[scenario].label} case: {describeScenario(scenario)} vs. your inputs.
          </p>
        )}

        {/* Headline returns */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 2xl:grid-cols-6">
          <Metric label="IRR (5-yr)" value={result ? irrLabel(result) : "—"} icon={TrendingUp} tone={result ? irrTone(result.irr, result.outcome) : undefined} />
          <Metric label="Equity Multiple" value={result ? mult(result.moic) : "—"} icon={DollarSign} tone={result && result.outcome !== "profit" ? "risk" : undefined} />
          <Metric label="Cash-on-Cash Yr 1" value={result ? pctText(result.cashOnCashY1) : "—"} icon={Percent} />
          <Metric label="Min Lender DSCR" value={result ? mult(result.minLenderDscr) : "—"} icon={Gauge} tone={result ? dscrTone(result.minLenderDscr) : undefined} />
          {result && result.totalShortfall > 0 ? (
            <Metric label="Cash You Add" value={compact(result.totalShortfall)} icon={AlertTriangle} tone="risk" />
          ) : (
            <Metric label="Payback" value={result ? (result.paybackYear ? `Year ${result.paybackYear}` : "> 5 yrs") : "—"} icon={Timer} />
          )}
          <Metric label="Net Exit Proceeds" value={result ? compact(result.exit.netProceeds) : "—"} icon={LogOut} tone={result && result.exit.netProceeds < 0 ? "risk" : undefined} />
        </div>

        {/* Loss / shortfall warning */}
        {result && (result.outcome !== "profit" || result.totalShortfall > 0) && (
          <div className={cn("flex items-start gap-3 rounded-2xl border px-4 py-3", toneClasses.risk)}>
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-sm leading-6 text-foreground">
              <span className="font-bold">
                {result.outcome === "total-loss" ? "This deal loses everything you put in" : result.outcome === "loss" ? "This deal loses money" : "Cash flow falls short"}
              </span>
              <span className="text-muted-foreground">
                {" — "}
                {result.totalShortfall > 0
                  ? `you'd need to add ${usd(result.totalShortfall)} beyond your ${usd(result.equity)} down payment to keep up with the debt. `
                  : ""}
                {result.outcome !== "profit" ? `Total loss over the hold: ${usd(-result.totalProfit)}.` : "The exit still makes it whole."}
              </span>
            </p>
          </div>
        )}

        {/* SBA eligibility */}
        {sba && sba.status !== "no-loan" && (
          <div className={cn("flex items-start gap-3 rounded-2xl border px-4 py-3", toneClasses[sba.status === "eligible" ? "excellent" : "ok"])}>
            {sba.status === "eligible" ? <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <p className="text-sm leading-6 text-foreground">
              <span className="font-bold">{sba.status === "eligible" ? "Likely SBA 7(a) eligible" : "Review needed for SBA 7(a)"}</span>
              <span className="text-muted-foreground"> — {sba.status === "eligible" ? "loan size, equity injection, term and Year 1 DSCR clear the common thresholds." : sba.reasons.join(" · ")}</span>
            </p>
          </div>
        )}

        {/* Pro forma */}
        <Card className="rounded-3xl">
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-lg">{HOLD_YEARS}-Year Pro Forma</CardTitle>
            {scenario !== "base" && (
              <span className="rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                {SCENARIO_SHIFTS[scenario].label} case
              </span>
            )}
          </CardHeader>
          <CardContent>
            {!result ? (
              <p className="text-sm text-muted-foreground">Fix the highlighted inputs to build the model.</p>
            ) : (
              <div className="-mx-2 overflow-x-auto">
                <table className="w-full min-w-[34rem] text-sm tabular-nums">
                  <thead>
                    <tr className="border-b border-border/60 text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-2 py-2 text-left font-semibold">Metric</th>
                      {years.map((y) => (
                        <th key={y.year} className={cn("px-2 py-2 text-right font-semibold", y.shocked && "text-amber-500")}>
                          Yr {y.year}
                          {y.shocked && <CloudLightning className="ml-1 inline h-3 w-3 align-[-1px]" aria-label="bad year" />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <TRow label="Revenue" cells={years.map((y) => compact(y.revenue))} />
                    {isSde && (
                      <>
                        <TRow label="SDE" cells={years.map((y) => compact(y.earnings))} />
                        <TRow label="Less: your salary" cells={years.map(() => compact(inputs!.ownerSalary))} muted />
                      </>
                    )}
                    <TRow label="EBITDA" cells={years.map((y) => compact(y.ebitda))} negative={years.map((y) => y.ebitda < 0)} />
                    {/* Costs are shown as positive amounts under a "Less:" label — never a double negative. */}
                    <TRow label="Less: capex" cells={years.map((y) => compact(y.capex))} muted />
                    <TRow label="Less: taxes" cells={years.map((y) => compact(y.taxes))} muted />
                    <TRow label="Less: SBA debt service" cells={years.map((y) => compact(y.sbaDebtService))} muted />
                    {result.sba.sellerNote > 0 && (
                      <TRow
                        label={flags.sellerNoteStandby ? "Less: seller note (standby)" : "Less: seller note"}
                        cells={years.map((y) => compact(y.noteDebtService))}
                        muted
                      />
                    )}
                    <tr className="border-y border-primary/25 bg-primary/10">
                      <td className="px-2 py-2.5 font-bold text-primary">Free Cash Flow</td>
                      {years.map((y) => (
                        <td key={y.year} className={cn("px-2 py-2.5 text-right font-mono font-bold", y.freeCashFlow < 0 ? "text-rose-500" : "text-primary")}>
                          {compact(y.freeCashFlow)}
                        </td>
                      ))}
                    </tr>
                    {anyReserveDraw && <TRow label="Covered by reserve" cells={years.map((y) => compact(y.reserveDraw))} muted />}
                    {anyShortfall && (
                      <TRow label="Cash you add" cells={years.map((y) => compact(y.shortfall))} negative={years.map((y) => y.shortfall > 0)} />
                    )}
                    <TRow label="Cumulative cash to you" cells={years.map((y) => compact(y.cumulativeBuyerCash))} negative={years.map((y) => y.cumulativeBuyerCash < 0)} />
                    <DscrRow label="DSCR (EBITDA)" values={years.map((y) => y.dscr)} />
                    <DscrRow label="Lender DSCR" hint="after capex & taxes" values={years.map((y) => y.lenderDscr)} />
                    <TRow label="Debt balance (yr end)" cells={years.map((y) => compact(y.sbaBalance + y.noteBalance))} muted last />
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Exit waterfall */}
          <Card className="rounded-3xl">
            <CardHeader>
              <CardTitle className="text-lg">Exit at end of Year {HOLD_YEARS}</CardTitle>
            </CardHeader>
            <CardContent>
              <Row label={inputs ? `Exit value (${inputs.exitMultiple.toFixed(1)}× Yr ${HOLD_YEARS} EBITDA)` : "Exit value"} value={result ? usd(result.exit.exitValue) : "—"} />
              <Row label="Less: selling costs" value={result ? usd(result.exit.exitCosts) : "—"} />
              <Row label="Less: SBA loan payoff" value={result ? usd(result.exit.sbaPayoff) : "—"} />
              {result && result.exit.notePayoff > 0 && <Row label="Less: seller note payoff" value={usd(result.exit.notePayoff)} />}
              {result && result.exit.reserveReturned > 0 && <Row label="Plus: unused reserve" value={usd(result.exit.reserveReturned)} />}
              <Row label="Net proceeds to you" value={result ? usd(result.exit.netProceeds) : "—"} strong />
              <Row label={`Plus: ${HOLD_YEARS} years of cash to you`} value={result ? usd(years[HOLD_YEARS - 1].cumulativeBuyerCash) : "—"} />
              <Row label="Less: your down payment" value={result ? usd(result.equity) : "—"} />
              <Row label={result && result.totalProfit < 0 ? "Total loss" : "Total profit"} value={result ? usd(Math.abs(result.totalProfit)) : "—"} strong danger={!!result && result.totalProfit < 0} />
            </CardContent>
          </Card>

          {/* Insights */}
          <Card className="rounded-3xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lightbulb className="h-4 w-4 text-primary" /> Model insights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {insights.length === 0 && <p className="text-sm text-muted-foreground">Fix the highlighted inputs to see insights.</p>}
              {insights.map((ins, k) => {
                const s = insightStyle[ins.tone];
                const Icon = s.icon;
                return (
                  <div key={k} className="flex items-start gap-2.5">
                    <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", s.cls)} />
                    <p className="text-sm leading-6 text-foreground">{ins.text}</p>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Scenario comparison */}
        {scenarios.length > 0 && (
          <Card className="rounded-3xl">
            <CardHeader>
              <CardTitle className="text-lg">Scenario comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
                {scenarios.map(({ key, r }) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={scenario === key}
                    onClick={() => setScenario(key)}
                    className={cn(
                      "rounded-2xl border p-4 text-left transition hover:border-primary/40",
                      scenario === key ? "border-primary/50 bg-primary/10" : "border-border/60 bg-card/70",
                    )}
                  >
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{SCENARIO_SHIFTS[key].label}</div>
                    <div className={cn("mt-1 font-mono text-2xl font-bold", toneText[irrTone(r.irr, r.outcome)])}>{irrLabel(r)}</div>
                    <div className="text-xs text-muted-foreground">IRR</div>
                    <div className="mt-3 space-y-1.5 text-sm">
                      <MiniRow label="Equity multiple" value={mult(r.moic)} />
                      <MiniRow label="Min lender DSCR" value={mult(r.minLenderDscr)} tone={dscrTone(r.minLenderDscr)} />
                      <MiniRow label="Cash you add" value={compact(r.totalShortfall)} tone={r.totalShortfall > 0 ? "risk" : undefined} />
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sensitivity */}
        {grid.length > 0 && inputs && (
          <Card className="rounded-3xl">
            <CardHeader>
              <CardTitle className="text-lg">IRR sensitivity</CardTitle>
              <p className="text-sm text-muted-foreground">Exit multiple (rows) vs. annual revenue growth (columns). Your current case is outlined.</p>
            </CardHeader>
            <CardContent>
              <div className="-mx-2 overflow-x-auto">
                <table className="w-full min-w-[30rem] text-sm tabular-nums">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-2 py-2 text-left font-semibold">Exit × \ Growth</th>
                      {SENSITIVITY_GROWTH_STEPS.map((dg) => (
                        <th key={dg} className="px-2 py-2 text-center font-mono font-semibold">{(inputs.revenueGrowthPct + dg).toFixed(1)}%</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grid.map((row, ri) => (
                      <tr key={ri}>
                        <td className="px-2 py-1.5 font-mono font-semibold text-muted-foreground">{row[0].multiple.toFixed(1)}×</td>
                        {row.map((cell, ci) => {
                          const isBase = SENSITIVITY_MULTIPLE_STEPS[ri] === 0 && SENSITIVITY_GROWTH_STEPS[ci] === 0;
                          return (
                            <td key={ci} className="p-1">
                              <div
                                className={cn(
                                  "rounded-lg border px-2 py-1.5 text-center font-mono text-sm font-bold",
                                  toneClasses[irrTone(cell.irr, cell.outcome)],
                                  isBase && "ring-2 ring-primary ring-offset-1 ring-offset-background",
                                )}
                              >
                                {irrLabel(cell)}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="px-1 text-[0.8125rem] leading-6 text-muted-foreground">
          Estimates only. Debt sizing and the SBA guaranty fee come from the same model as the SBA Calculator. Taxes are on
          EBITDA less interest paid, 7-year depreciation on the equipment share of the price and 15-year amortization on the
          rest, with losses carried forward (capped at 80% of later income). Accrued standby-note interest isn't deducted,
          which keeps taxes conservative. Lender DSCR subtracts capex and taxes from EBITDA, the way most SBA lenders
          underwrite. Confirm assumptions with your CPA and lender.
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-primary">{title}</div>
      {children}
    </div>
  );
}

function Pair({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>;
}

function Toggle({
  id,
  checked,
  onChange,
  icon: Icon,
  label,
  hint,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  icon: typeof DollarSign;
  label: string;
  hint: string;
}) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-primary" />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" /> {label}
        </span>
        <span className="mt-0.5 block text-[0.8125rem] leading-5 text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
}

function ActionButton({ onClick, disabled, icon: Icon, children }: { onClick: () => void; disabled?: boolean; icon: typeof Download; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-xl border border-primary/25 bg-primary/10 px-3.5 py-2 text-sm font-semibold text-primary transition hover:bg-primary/15 disabled:opacity-50"
    >
      <Icon className={cn("h-3.5 w-3.5", Icon === Loader2 && "animate-spin")} /> {children}
    </button>
  );
}

function Metric({ label, value, icon: Icon, tone }: { label: string; value: string; icon: typeof DollarSign; tone?: Tone }) {
  const highlight = tone && tone !== "neutral" ? toneClasses[tone] : undefined;
  return (
    <div className={cn("flex flex-col justify-between rounded-2xl border p-4", highlight ?? "border-border/60 bg-card/70")}>
      <div className={cn("flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide", highlight ? "opacity-80" : "text-muted-foreground")}>
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className={cn("mt-2 font-mono text-xl font-bold leading-tight tabular-nums", !highlight && "text-foreground")}>{value}</div>
    </div>
  );
}

function TRow({ label, cells, muted, last, negative }: { label: string; cells: string[]; muted?: boolean; last?: boolean; negative?: boolean[] }) {
  return (
    <tr className={cn(!last && "border-b border-border/40")}>
      <td className={cn("px-2 py-2.5", muted ? "text-muted-foreground" : "font-semibold text-foreground")}>{label}</td>
      {cells.map((c, k) => (
        <td
          key={k}
          className={cn("px-2 py-2.5 text-right font-mono", negative?.[k] ? "font-semibold text-rose-500" : muted ? "text-muted-foreground" : "font-semibold text-foreground")}
        >
          {c}
        </td>
      ))}
    </tr>
  );
}

function DscrRow({ label, hint, values }: { label: string; hint?: string; values: (number | null)[] }) {
  return (
    <tr className="border-b border-border/40">
      <td className="px-2 py-2.5 text-muted-foreground">
        {label}
        {hint && <span className="block text-xs leading-tight">{hint}</span>}
      </td>
      {values.map((v, k) => (
        <td key={k} className={cn("px-2 py-2.5 text-right font-mono font-semibold", toneText[dscrTone(v)])}>
          {v === null ? "n/a" : v.toFixed(2)}
        </td>
      ))}
    </tr>
  );
}

function Row({ label, value, strong, danger }: { label: string; value: string; strong?: boolean; danger?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/40 py-2.5 last:border-0">
      <span className={cn("text-sm", strong ? "font-bold text-foreground" : "text-muted-foreground")}>{label}</span>
      <span
        className={cn(
          "shrink-0 font-mono tabular-nums",
          strong ? "text-base font-extrabold" : "text-sm font-semibold text-foreground",
          strong && (danger ? "text-rose-500" : "text-primary"),
        )}
      >
        {value}
      </span>
    </div>
  );
}

function MiniRow({ label, value, tone }: { label: string; value: string; tone?: Tone }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-mono font-semibold tabular-nums", tone ? toneText[tone] : "text-foreground")}>{value}</span>
    </div>
  );
}
